import { LoggerBridge, nowMs } from '../audio/shared';
import { MicAcquireError, MicLease, MicSource } from './micSource';
import { OutputRouter } from './outputRouter';
import { INITIAL_TALK_SIGNAL_STATE, TalkSignal, TalkSignalState, mapDataChannelMessage } from './talkSignals';

export type VoiceCallState = 'starting' | 'connecting' | 'connected' | 'reconnecting' | 'ended' | 'failed';

export type VoiceCallErrorCode =
    | 'insecure-context'
    | 'webrtc-unsupported'
    | 'mic-permission-denied'
    | 'mic-unavailable'
    | 'mic-busy'
    | 'offer-failed'
    | 'negotiation-failed'
    | 'ice-failed'
    | 'connect-timeout'
    | 'connection-lost'
    | 'ended-by-remote';

// What the page learns about the call: the conversation signals plus playback and channel events.
export type CallSignal = TalkSignal | 'media-ready' | 'playback-blocked' | 'playback-resumed' | 'data-channel-error';

export interface VoiceCallStartOptions {
    iceServers: RTCIceServer[];
    micId: string | null;
    connectTimeoutMs: number;
    iceGatheringTimeoutMs?: number;
}

export interface VoiceCallStartResult {
    offerSdp: string | null;
    error: VoiceCallErrorCode | null;
}

export interface VoiceCallStats {
    candidateType: string | null;
    protocol: string | null;
    roundTripTimeMs: number | null;
    jitterMs: number | null;
    packetsLost: number | null;
    packetsReceived: number | null;
    bytesReceived: number | null;
    bytesSent: number | null;
}

// The data channel name of the OpenAI/Azure Realtime WebRTC samples.
const DATA_CHANNEL_LABEL = 'oai-events';
const DEFAULT_CONNECT_TIMEOUT_MS = 20000;
// There is no trickle ICE (the offer goes out in one HTTP exchange), so with our own STUN/TURN servers the
// offer waits this long for their candidates. Without them Azure's candidates are enough and nothing waits.
const DEFAULT_ICE_GATHERING_TIMEOUT_MS = 2500;
// How long a dropped connection may take to come back before the call counts as lost, how often the stats
// are checked meanwhile, and the longest that arriving media may stretch the grace.
const DISCONNECTED_GRACE_MS = 8000;
const DISCONNECTED_STATS_POLL_MS = 2000;
const DISCONNECTED_MAX_MS = 24000;
// Events that count as a user gesture for HTMLMediaElement.play() in Chromium, Firefox and Safari.
const UNLOCK_EVENTS = ['click', 'touchend', 'keydown', 'pointerup'];

// Callbacks into the C# VoiceCall object. Calls are chained so .NET sees them in the order they happened.
export class CallBridge {
    private chain: Promise<unknown> = Promise.resolve();

    constructor(private readonly ref: any) { }

    stateChanged(state: VoiceCallState, code: VoiceCallErrorCode | null): void {
        this.enqueue('OnCallStateChanged', state, code);
    }

    signal(signal: CallSignal): void {
        this.enqueue('OnTalkSignal', signal);
    }

    private enqueue(method: string, ...args: unknown[]): void {
        this.chain = this.chain
            .then(() => this.ref?.invokeMethodAsync(method, ...args))
            .catch(() => { }); // the .NET side is gone (disposed or disconnected)
    }
}

// Summarizes the WebRTC stats a support engineer needs: how the call connected and how the audio arrives.
export function summarizeStats(report: RTCStatsReport): VoiceCallStats {
    let selectedPairId: string | null = null;
    const pairs: any[] = [];
    const localCandidates = new Map<string, any>();
    let inbound: any = null;
    let outbound: any = null;
    report.forEach((stat: any) => {
        switch (stat.type) {
            case 'transport':
                if (stat.selectedCandidatePairId) selectedPairId = stat.selectedCandidatePairId;
                break;
            case 'candidate-pair':
                pairs.push(stat);
                break;
            case 'local-candidate':
                localCandidates.set(stat.id, stat);
                break;
            case 'inbound-rtp':
                if (stat.kind === 'audio') inbound = stat;
                break;
            case 'outbound-rtp':
                if (stat.kind === 'audio') outbound = stat;
                break;
        }
    });
    const pair = (selectedPairId ? pairs.find(p => p.id === selectedPairId) : null)
        ?? pairs.find(p => p.nominated && p.state === 'succeeded')
        ?? pairs.find(p => p.selected)
        ?? null;
    const local = pair ? localCandidates.get(pair.localCandidateId) : null;
    return {
        candidateType: local?.candidateType ?? null,
        protocol: local?.protocol ?? null,
        roundTripTimeMs: typeof pair?.currentRoundTripTime === 'number' ? Math.round(pair.currentRoundTripTime * 1000) : null,
        jitterMs: typeof inbound?.jitter === 'number' ? Math.round(inbound.jitter * 1000) : null,
        packetsLost: inbound?.packetsLost ?? null,
        packetsReceived: inbound?.packetsReceived ?? null,
        bytesReceived: inbound?.bytesReceived ?? null,
        bytesSent: outbound?.bytesSent ?? null
    };
}

// Resolves once ICE gathering completes, or after the timeout with whatever was gathered so far.
function waitForIceGathering(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise(resolve => {
        const done = () => {
            clearTimeout(timer);
            pc.removeEventListener('icegatheringstatechange', onChange);
            resolve();
        };
        const onChange = () => {
            if (pc.iceGatheringState === 'complete') done();
        };
        const timer = window.setTimeout(done, timeoutMs);
        pc.addEventListener('icegatheringstatechange', onChange);
    });
}

// Firefox before 113 only reports ICE states.
function fromIceState(state: RTCIceConnectionState): string {
    switch (state) {
        case 'connected':
        case 'completed':
            return 'connected';
        default:
            return state;
    }
}

// One WebRTC call between the browser and Azure OpenAI. Our server creates the call from this session's SDP
// offer and keeps its own (sideband) connection to it; the browser carries the audio and reads the
// conversation signals from the data channel. Nothing is ever sent on the data channel: interrupts and
// instructions go through our server. SDP and transcripts are never logged.
export class VoiceCallSession {
    private currentState: VoiceCallState = 'starting';
    private pc: RTCPeerConnection | null = null;
    private channel: RTCDataChannel | null = null;
    private sender: RTCRtpSender | null = null;
    private lease: MicLease | null = null;
    private audio: HTMLAudioElement | null = null;
    private signalState: TalkSignalState = INITIAL_TALK_SIGNAL_STATE;
    private connectTimeoutMs = DEFAULT_CONNECT_TIMEOUT_MS;
    private connectTimer: number | null = null;
    private disconnectTimer: number | null = null;
    private unlockHandler: (() => void) | null = null;
    private teardownTask: Promise<void> | null = null;
    private everConnected = false;
    private channelOpen = false;
    private playing = false;
    private playbackBlocked = false;
    private everBlocked = false;
    private mediaReadySent = false;
    private micMuted = false;
    private outputMuted = false;
    private endCode: VoiceCallErrorCode | null = null;
    private candidateType: string | null = null;
    private protocol: string | null = null;
    private reconnects = 0;
    private speechTurns = 0;
    private answers = 0;
    private readonly startedAt = nowMs();
    private offeredAt: number | null = null;
    private answeredAt: number | null = null;
    private connectedAt: number | null = null;
    private mediaReadyAt: number | null = null;
    private firstAudioAt: number | null = null;
    private endedAt: number | null = null;

    constructor(
        readonly id: string,
        private readonly bridge: CallBridge,
        private readonly mic: MicSource,
        private readonly output: OutputRouter,
        private readonly logger: LoggerBridge) { }

    get state(): VoiceCallState {
        return this.currentState;
    }

    get isFinished(): boolean {
        return this.currentState === 'ended' || this.currentState === 'failed';
    }

    // Opens the microphone and creates the SDP offer for our server to send to Azure.
    async start(options: VoiceCallStartOptions): Promise<VoiceCallStartResult> {
        if (typeof window !== 'undefined' && window.isSecureContext === false) {
            return this.failStart('insecure-context');
        }
        if (typeof RTCPeerConnection === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
            return this.failStart('webrtc-unsupported');
        }
        this.connectTimeoutMs = options.connectTimeoutMs > 0 ? options.connectTimeoutMs : DEFAULT_CONNECT_TIMEOUT_MS;

        try {
            this.lease = await this.mic.lease(options.micId);
        } catch (e) {
            return this.failStart(e instanceof MicAcquireError ? e.code : 'mic-unavailable', e);
        }
        if (this.isFinished) {
            // Stopped while the permission prompt was open.
            await this.releaseLease();
            return { offerSdp: null, error: null };
        }

        const iceServers = options.iceServers ?? [];
        let offerSdp: string | null;
        try {
            const pc = new RTCPeerConnection({ iceServers });
            this.pc = pc;
            const lease = this.lease;
            if (this.micMuted) lease.setMuted(true);
            this.sender = pc.addTrack(lease.track, new MediaStream([lease.track]));
            lease.onReplace(track => this.sender ? this.sender.replaceTrack(track) : Promise.resolve());

            // The data channel must exist before the offer, or the offer carries no SCTP section.
            const channel = pc.createDataChannel(DATA_CHANNEL_LABEL);
            this.channel = channel;
            channel.onopen = () => this.onChannelOpen();
            channel.onclose = () => this.onChannelClose();
            channel.onerror = event => this.onChannelError(event);
            channel.onmessage = event => this.onChannelMessage(event.data);

            pc.ontrack = event => this.onRemoteTrack(event);
            pc.onconnectionstatechange = () => this.onConnectionStateChange();
            pc.oniceconnectionstatechange = () => this.onConnectionStateChange();

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            if (iceServers.length > 0) {
                await waitForIceGathering(pc, options.iceGatheringTimeoutMs ?? DEFAULT_ICE_GATHERING_TIMEOUT_MS);
            }
            offerSdp = pc.localDescription?.sdp ?? offer.sdp ?? null;
        } catch (e) {
            return this.failStart('offer-failed', e);
        }

        if (this.isFinished) return { offerSdp: null, error: null };
        if (!offerSdp) return this.failStart('offer-failed');
        this.offeredAt = nowMs();
        return { offerSdp, error: null };
    }

    // Applies Azure's SDP answer exactly as received. The call is connected once the peer connection is
    // connected AND the data channel is open.
    async applyAnswer(answerSdp: string): Promise<boolean> {
        if (!this.pc || this.isFinished) return false;
        try {
            await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
        } catch (e) {
            await this.logger.error('Applying the voice call answer failed', String((e as any)?.name ?? e), { callId: this.id });
            // The caller learns it from the result.
            void this.finish('negotiation-failed', false);
            return false;
        }
        if (this.isFinished) return false;
        this.answeredAt = nowMs();
        this.setState('connecting');
        this.connectTimer = window.setTimeout(() => this.onConnectTimeout(), this.connectTimeoutMs);
        // A concrete mic id means a deliberate device choice: pair the output to THAT mic.
        void this.output.route(this.mic.currentMicId, this.mic.currentMicId !== null);
        return true;
    }

    setMuted(muted: boolean): void {
        this.micMuted = muted;
        this.lease?.setMuted(muted);
    }

    setOutputMuted(muted: boolean): void {
        this.outputMuted = muted;
        if (this.audio) this.audio.muted = muted;
    }

    resumePlayback(): Promise<void> {
        return this.play();
    }

    // Our page ends the call (after it told the server). Idempotent.
    stop(): Promise<void> {
        return this.finish(null, false);
    }

    // The page is being unloaded: nobody is left to tell, so Azure gets the call back right away.
    stopForPageHide(): Promise<void> {
        void this.logger.info('Voice call closed by page hide', { callId: this.id });
        return this.finish(null, false);
    }

    async getStats(): Promise<VoiceCallStats | null> {
        if (!this.pc || this.isFinished) return null;
        try {
            return summarizeStats(await this.pc.getStats());
        } catch {
            return null;
        }
    }

    snapshot(): Record<string, unknown> {
        return {
            id: this.id,
            state: this.currentState,
            endCode: this.endCode,
            signalingState: this.pc?.signalingState ?? null,
            iceGatheringState: this.pc?.iceGatheringState ?? null,
            iceConnectionState: this.pc?.iceConnectionState ?? null,
            connectionState: this.pc?.connectionState ?? null,
            dataChannelState: this.channel?.readyState ?? null,
            micTrackEnabled: this.lease ? this.lease.track.enabled : null,
            micTrackState: this.lease ? this.lease.track.readyState : null,
            micMuted: this.micMuted,
            outputMuted: this.outputMuted,
            remoteTrack: !!this.audio?.srcObject,
            playing: this.playing,
            playbackBlocked: this.playbackBlocked,
            mediaReady: this.mediaReadySent,
            outputActive: this.signalState.outputActive,
            sinkId: (this.audio as any)?.sinkId ?? null,
            candidateType: this.candidateType,
            reconnects: this.reconnects
        };
    }

    private onRemoteTrack(event: RTCTrackEvent): void {
        if (event.track.kind !== 'audio' || this.isFinished) return;
        const audio = this.audio ?? this.output.createElement();
        this.audio = audio;
        audio.muted = this.outputMuted;
        audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
        void this.output.applyCurrentSink(audio);
        void this.play();
    }

    private async play(): Promise<void> {
        const audio = this.audio;
        if (!audio || this.isFinished) return;
        try {
            await audio.play();
        } catch (e: any) {
            if (e?.name === 'NotAllowedError') {
                if (!this.playbackBlocked) {
                    this.playbackBlocked = true;
                    this.everBlocked = true;
                    this.installUnlock();
                    this.bridge.signal('playback-blocked');
                    void this.logger.warning('Voice call playback blocked until the user interacts', { callId: this.id });
                }
            } else if (e?.name !== 'AbortError') {
                void this.logger.warning('Voice call playback failed', { callId: this.id, error: String(e?.name ?? e) });
            }
            return;
        }
        if (this.isFinished) return;
        this.playing = true;
        if (this.playbackBlocked) {
            this.playbackBlocked = false;
            this.removeUnlock();
            this.bridge.signal('playback-resumed');
        }
        this.checkMediaReady();
    }

    // With autoplay blocked, the next tap or key press anywhere on the page starts playback. play() has to
    // run inside that event, so it can't wait for a round trip to .NET (Blazor Server).
    private installUnlock(): void {
        if (this.unlockHandler) return;
        const handler = () => { void this.play(); };
        this.unlockHandler = handler;
        for (const type of UNLOCK_EVENTS) document.addEventListener(type, handler, true);
    }

    private removeUnlock(): void {
        const handler = this.unlockHandler;
        if (!handler) return;
        this.unlockHandler = null;
        for (const type of UNLOCK_EVENTS) document.removeEventListener(type, handler, true);
    }

    private onChannelOpen(): void {
        this.channelOpen = true;
        this.updateConnected();
    }

    private onChannelClose(): void {
        const wasOpen = this.channelOpen;
        this.channelOpen = false;
        // Azure closes the data channel when the call ends: our server's hangup (limits, close) or its own cap.
        if (wasOpen && !this.isFinished) void this.finish('ended-by-remote');
    }

    private onChannelError(event: Event): void {
        if (this.isFinished) return;
        const error = (event as any)?.error;
        void this.logger.warning('Voice call data channel error', { callId: this.id, error: String(error?.errorDetail ?? error?.name ?? 'unknown') });
        this.bridge.signal('data-channel-error');
    }

    private onChannelMessage(data: unknown): void {
        if (this.isFinished) return;
        const result = mapDataChannelMessage(data, this.signalState);
        this.signalState = result.state;
        for (const signal of result.signals) {
            if (signal === 'speech-started') this.speechTurns++;
            if (signal === 'output-started') {
                this.answers++;
                if (this.firstAudioAt === null) this.firstAudioAt = nowMs();
            }
            this.bridge.signal(signal);
        }
    }

    private onConnectionStateChange(): void {
        const pc = this.pc;
        if (!pc || this.isFinished) return;
        const state = typeof pc.connectionState === 'string' ? pc.connectionState : fromIceState(pc.iceConnectionState);
        if (state === 'connected') {
            if (this.disconnectTimer !== null) {
                this.clearDisconnectTimer();
                void this.logger.info('Voice call connection recovered', { callId: this.id });
            }
            if (!this.everConnected) {
                this.everConnected = true;
                this.connectedAt = nowMs();
                void this.recordConnection();
            }
            this.updateConnected();
        } else if (state === 'disconnected') {
            this.onDisconnected();
        } else if (state === 'failed') {
            void this.finish(this.everConnected ? 'connection-lost' : 'ice-failed');
        }
    }

    // 'disconnected' often heals by itself (Wi-Fi roaming, a short outage). Give it a grace period and keep it
    // alive longer while media still arrives; then the call counts as lost.
    private onDisconnected(): void {
        if (this.disconnectTimer !== null) return;
        const since = nowMs();
        let deadline = since + DISCONNECTED_GRACE_MS;
        let lastBytesReceived: number | null = null;
        let polling = false;
        if (this.currentState === 'connected') {
            this.reconnects++;
            this.setState('reconnecting');
        }
        void this.logger.warning('Voice call connection interrupted', { callId: this.id, everConnected: this.everConnected });
        this.disconnectTimer = window.setInterval(async () => {
            if (polling || this.isFinished) return;
            polling = true;
            try {
                const bytes = (await this.getStats())?.bytesReceived ?? null;
                const now = nowMs();
                if (bytes !== null && lastBytesReceived !== null && bytes > lastBytesReceived) {
                    deadline = Math.min(now + DISCONNECTED_GRACE_MS, since + DISCONNECTED_MAX_MS);
                }
                lastBytesReceived = bytes;
                if (now >= deadline && this.disconnectTimer !== null && !this.isFinished) {
                    this.clearDisconnectTimer();
                    void this.finish(this.everConnected ? 'connection-lost' : 'ice-failed');
                }
            } finally {
                polling = false;
            }
        }, DISCONNECTED_STATS_POLL_MS);
    }

    private onConnectTimeout(): void {
        this.connectTimer = null;
        if (this.isFinished || this.currentState === 'connected') return;
        // ICE that never connects usually means UDP/TCP 3478 to Azure is blocked on this network; ICE that
        // connected without the data channel opening is a plain timeout.
        void this.finish(this.everConnected ? 'connect-timeout' : 'ice-failed');
    }

    private updateConnected(): void {
        if (this.isFinished) return;
        if (this.currentState !== 'connected' && this.everConnected && this.channelOpen && this.disconnectTimer === null) {
            this.clearConnectTimer();
            this.setState('connected');
        }
        this.checkMediaReady();
    }

    // Media is ready once the call is connected and the agent's audio actually plays. Only then may the server
    // greet the user; a greeting into blocked playback would be lost.
    private checkMediaReady(): void {
        if (this.mediaReadySent || this.isFinished || this.currentState !== 'connected' || !this.playing) return;
        this.mediaReadySent = true;
        this.mediaReadyAt = nowMs();
        this.bridge.signal('media-ready');
    }

    private async recordConnection(): Promise<void> {
        const stats = await this.getStats();
        this.candidateType = stats?.candidateType ?? null;
        this.protocol = stats?.protocol ?? null;
        await this.logger.info('Voice call connected', {
            callId: this.id,
            candidateType: this.candidateType,
            protocol: this.protocol,
            msFromAnswerToConnected: this.answeredAt !== null && this.connectedAt !== null ? Math.round(this.connectedAt - this.answeredAt) : null
        });
    }

    private setState(state: VoiceCallState): void {
        this.currentState = state;
        this.bridge.stateChanged(state, null);
    }

    private async failStart(code: VoiceCallErrorCode, error?: unknown): Promise<VoiceCallStartResult> {
        await this.logger.warning('Voice call could not start', { callId: this.id, code, error: error === undefined ? null : String((error as any)?.name ?? error) });
        // The caller gets the error from the result; no separate state callback.
        await this.finish(code, false);
        return { offerSdp: null, error: code };
    }

    private finish(code: VoiceCallErrorCode | null, notify = true): Promise<void> {
        if (this.isFinished) return this.teardownTask ?? Promise.resolve();
        this.endCode = code;
        this.currentState = code === null || code === 'ended-by-remote' ? 'ended' : 'failed';
        this.endedAt = nowMs();
        this.clearConnectTimer();
        this.clearDisconnectTimer();
        this.removeUnlock();

        const channel = this.channel;
        this.channel = null;
        if (channel) {
            channel.onopen = channel.onclose = channel.onerror = channel.onmessage = null;
            try { channel.close(); } catch { }
        }
        const pc = this.pc;
        this.pc = null;
        if (pc) {
            pc.ontrack = pc.onconnectionstatechange = pc.oniceconnectionstatechange = null;
            try { pc.close(); } catch { }
        }
        this.sender = null;
        if (this.audio) {
            this.output.releaseElement(this.audio);
            this.audio = null;
        }

        if (notify) this.bridge.stateChanged(this.currentState, code);
        void this.logSummary();
        this.teardownTask = this.releaseLease();
        return this.teardownTask;
    }

    private async releaseLease(): Promise<void> {
        const lease = this.lease;
        this.lease = null;
        await lease?.release();
    }

    private async logSummary(): Promise<void> {
        const since = (at: number | null) => at === null ? null : Math.round(at - this.startedAt);
        await this.logger.info('VoiceCall.Summary', {
            callId: this.id,
            endCode: this.endCode ?? 'stopped',
            durationMs: since(this.endedAt),
            msToOffer: since(this.offeredAt),
            msToAnswer: since(this.answeredAt),
            msToConnected: since(this.connectedAt),
            msToMediaReady: since(this.mediaReadyAt),
            msToFirstAudio: since(this.firstAudioAt),
            candidateType: this.candidateType,
            protocol: this.protocol,
            playbackBlocked: this.everBlocked,
            reconnects: this.reconnects,
            speechTurns: this.speechTurns,
            answers: this.answers
        });
    }

    private clearConnectTimer(): void {
        if (this.connectTimer !== null) {
            clearTimeout(this.connectTimer);
            this.connectTimer = null;
        }
    }

    private clearDisconnectTimer(): void {
        if (this.disconnectTimer !== null) {
            clearInterval(this.disconnectTimer);
            this.disconnectTimer = null;
        }
    }
}
